// /findings wire schemas. Edit and delete only — creates happen as a side
// effect of /reports/:id/complete (AI generates them) or via the capture
// flow. The /delegate sub-route is post-signing — it's the path the
// gerencial dashboard uses to assign findings to external providers,
// which is intentionally allowed even after the parent report is signed.
import { unitVisitContactSchema } from "./visit-contact.js";
import { CLASSIFICATION_AXIS_CODES, clientSettableDelegationStatusSchema, delegationStatusSchema, DERIVED_SEVERITIES, EVIDENCE_CAPTURE_ROLES, evidenceSchema, evidenceTypeSchema, findingIdSchema, findingSchema, findingUpdateSchema, FUENTES_EJE, INTAKE_CHANNELS, OBSERVATION_PHASES, OBSERVATION_VISIT_CONFIRMATIONS, OBSERVATION_VISIT_PREFERRED_DAYS_MAX, OBSERVATION_VISIT_PROPOSERS, OBSERVATION_VISIT_SLOTS, observationVisitDateSchema, observationVisitTimeSchema, visitRangeValid, RECLASSIFY_ATRIBUCION_CODES, taxonomyHintsSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
import { assetObservationSchema, observationClosedReasonSchema, observationVisitSchema, } from "./assets.js";
import { emailSchema } from "./auth.js";
// `taxonomyOptionSchema` vivía acá; se mudó a common.ts cuando el feed
// de observaciones (assets.ts) pasó a necesitarlo también — ver el
// docstring allá para por qué el ciclo assets↔findings no era una
// opción. `ejeFuenteSchema` y `taxonomyOptionsSchema` siguieron el
// mismo camino y por la misma razón cuando F5 (carril del propietario)
// los necesitó en `assets.ts`. Se importan, no se re-exportan: index.ts
// ya hace `export * from "./common.js"`, así que los nombres siguen
// saliendo del root del paquete exactamente igual que antes.
import { ejeFuenteSchema, ownerDescriptionSchema, severityOriginSchema, taxonomyOptionsSchema, timelineActorLaneSchema, } from "./common.js";
// #0646 — `falta` ya tenía UN schema en reports.ts, cuyo propio
// comentario declara como tercer consumidor el read model de
// GET /findings/:id… que sin embargo lo re-tipeaba acá a mano como
// `{ code: z.string(), label: z.string() }`. Se importa en vez de
// re-declararse; `confianzaFaltaCodeSchema` es el vocabulario a secas,
// para el `gate.falta` de /reclassify que no lleva etiqueta.
import { ownerNoticeOutcomeSchema } from "./owner-notifications.js";
import { PUBLIC_VISIT_REASONS } from "./public-bundle.js";
import { confianzaFaltaCodeSchema, confianzaFaltaSchema } from "./reports.js";
import { criticalAlertMarkSchema } from "./critical-alerts.js";
export const findingParams = z.object({ id: findingIdSchema });
export const findingUpdateBody = findingUpdateSchema;
export const findingResponse = findingSchema;
// Note collapse, shared by every free-text field on this route family
// that a desktop textarea can reach — findingDelegationStatusBody.note
// (below) already applies it; findingDelegateBody.note was the one gap
// (security-engineer, Arrow pilot review 2026-08-10): a newline typed
// into AssignDrawer's textarea would otherwise reach the notification
// mail subject built by raw interpolation.
const collapseNewlines = (v) => v.replace(/[\r\n]+/g, " ").trim();
// Inline provider creation (Arrow pilot, 2026-08-10). Oliver declined
// the pre-loaded providers module; the resolution is not a free-text
// recipient (see docs/decisions/0082 — that framing was withdrawn) but
// a real, org-scoped `providers` row created inline from the assign
// drawer. `shortName`/`specialty` mirror providers' own column
// constraints (specialty now nullable, schema.ts) trimmed at the
// boundary for the same catalog-pollution reason as email lower-casing.
//
// Design pull (2026-08-12, founder decision) replaces the free-text
// "especialidad" input with a "Categorías que atiende" multiselect and
// adds a region field; the DB still has one `specialty` text column, so
// the dashboard joins the selected categories into it (semicolon-
// separated, matching the canvas's own field hint) rather than this
// schema growing a new array field for a column that doesn't exist yet.
// `defaultSlaDays` is dropped from the write surface entirely — the SLA
// feature is being removed from the product (founder decision), not
// just this form; the DB column stays (existing providers keep whatever
// value they have), but nothing writes it anymore.
const newProviderSchema = z.object({
    shortName: z.string().trim().min(1).max(200),
    email: emailSchema,
    specialty: z.string().trim().min(1).max(200).optional(),
    region: z.string().trim().min(1).max(200).optional(),
});
// A discriminated union rather than two calls (create-then-delegate):
// react-expert's Phase B finding — a two-call sequence has a real
// orphan-write failure mode (create succeeds, delegate fails, retry
// duplicates) that a single transactional write avoids entirely.
export const findingDelegateBody = z
    .object({
    providerId: z.string().uuid().optional(),
    newProvider: newProviderSchema.optional(),
    note: z.string().max(500).transform(collapseNewlines).optional(),
    notifyStoreManager: z.boolean().optional(),
})
    // .strict() (security-engineer): unknown keys must reject rather than
    // silently strip, so "the recipient cannot be redirected by the body"
    // is structurally true instead of incidentally true.
    .strict()
    .refine((b) => Boolean(b.providerId) !== Boolean(b.newProvider), {
    message: "exactly one of providerId or newProvider must be provided",
});
export const findingDelegateResponse = z.object({
    id: z.string().uuid(),
    providerId: z.string().uuid(),
    // Set only when the request carried `newProvider` — true if that
    // provider row was newly inserted, false if it normalized onto an
    // existing org provider (ON CONFLICT ... DO NOTHING + attach, per
    // data-engineer) and the request attached to that row instead.
    // Null when the request carried `providerId` directly (nothing was
    // resolved). The dashboard's success copy branches on this rather
    // than always claiming "proveedor creado" — a false claim on the
    // attach path (frontend-engineer Phase B synthesis, 2026-08-10).
    providerCreated: z.boolean().nullable(),
    // Derived from the engine rather than a bare z.string(): the route
    // returns the literal "pendiente" and nothing else can appear here.
    delegationStatus: delegationStatusSchema,
    delegatedAt: z.string().datetime(),
});
// POST /findings/:id/delegation-status (ADR-0074) — moves a finding
// through the provider-delegation lifecycle. Third deliberate carve-out
// from the signed-report freeze, on the same basis as /delegate and
// /observation-status below: a signed acta renders from
// reports.findingsSnapshot, frozen at sign time, so a live status change
// is invisible to the signed document.
//
// `status` derives from the engine's enum (unlike
// findingObservationStatusBody's loose string, which is loose *because*
// its vocabulary is recipe-declared and unknowable at compile time —
// that asymmetry is the boundary, not drift). `note` is wire-only with
// no engine counterpart, the same exception findingDelegateBody's
// notifyStoreManager already uses.
export const findingDelegationStatusBody = z
    .object({
    status: clientSettableDelegationStatusSchema,
    note: z
        .string()
        .max(500)
        // Authored in a desktop textarea, unlike the mobile-dictated
        // siblings, so embedded newlines are newly reachable. Collapse
        // them at the boundary rather than letting them into a log line.
        .transform((v) => v.replace(/[\r\n]+/g, " ").trim())
        .optional(),
})
    .strict();
export const findingDelegationStatusResponse = z.object({
    id: z.string().uuid(),
    delegationStatus: delegationStatusSchema,
    delegatedAt: z.string().datetime().nullable(),
    // Non-null iff delegationStatus === "resuelto". /facility-dashboard's
    // resolvedCount filters on this column alone, with no secondary status
    // check, so the two must never disagree.
    resolvedAt: z.string().datetime().nullable(),
});
// POST /findings/:id/observation-status (ADR-0058) — the asset-scoped
// observation lifecycle, distinct from /delegate above: no provider
// involved, this is the inmobiliaria's own warranty-remediation
// tracking (broker_postventa's observationStatuses vocabulary).
// Deliberately allowed post-signing, same carve-out as /delegate — see
// the route's own comment for why that's safe (reports.findingsSnapshot,
// ADR-0058).
export const findingObservationStatusBody = z
    .object({
    status: z.string().min(1).max(64),
    note: z.string().max(500).optional(),
    /** ADR-0134 §3 — evidencia del hito: fotos ya subidas (presign de
     *  media-svc), con el mismo par de validación que `photoUrls` de
     *  /conformity (origen permitido + clave de objeto de la org). Cuelgan
     *  de la fila de `finding_events` que esta transición escribe
     *  (`evidence.finding_event_id`). Un destino que declara
     *  `requiresEvidence` (facility: «resuelta», el jefe de tienda que lo
     *  arregla él mismo) exige al menos una: 400
     *  OBSERVATION_EVIDENCE_REQUIRED. Ausente = sin evidencia. */
    photoUrls: z.array(z.string().url()).max(8).optional(),
})
    .strict();
export const findingObservationStatusResponse = z.object({
    id: z.string().uuid(),
    observationStatus: z.string(),
    observationStatusAt: z.string().datetime(),
    /** «asset party notified»: true sólo cuando auth-svc confirmó el envío
     *  del correo al asset_party de la observación — el propietario en
     *  postventa (aceptada / rechazada), el jefe de tienda en facility
     *  (anulada, ADR-0134 §9). El nombre quedó de cuando sólo existía el
     *  propietario. False si el destino no avisa,
     *  la unidad no tiene propietario activado o el envío falló — el
     *  cliente no debe prometer «le avisamos» sin esto. Opcional: clientes
     *  desplegados no lo esperan. Con el correo apagado por el propietario
     *  (ADR-0147) es true: no es un fallo — `ownerNotice` lo distingue. */
    ownerNotified: z.boolean().optional(),
    /** ADR-0147 (corte de ajustes) — `sent` | `suppressed_by_preference` |
     *  `failed`. Ausente si el destino no le escribe al asset_party. El
     *  panel dice «El propietario eligió no recibir este aviso por correo»
     *  con `suppressed_by_preference`, nunca «se notificó». */
    ownerNotice: ownerNoticeOutcomeSchema.optional(),
});
// POST /findings/:id/conformity — el veredicto de la contraparte que
// recibió el trabajo. Es el ÚNICO escritor del overlay de cierre.
//
// Las dos ramas hacen cosas estructuralmente distintas, y por eso son
// una unión discriminada y no un booleano:
//
//   `conforme`      — estampa el overlay y NO TOCA `observation_status`.
//                     El cierre es un hito, no un sexto estado
//                     (ADR-0105 Decisión 4).
//   `no_conforme`   — sí mueve el estado (`resuelta → abierta`, la arista
//                     del grafo), incrementa el ciclo y exige constancia.
//
// La constancia de la no conformidad es **al menos una foto Y una nota
// de voz** (decisión 13, minuta 02) para el carril que la debe. El
// comentario escrito se SUMA, nunca sustituye: un rechazo del trabajo
// ajeno sin evidencia de qué sigue mal es justo lo que deja el hilo sin
// nada que el contratista pueda accionar.
//
// Desde ADR-0134 §5 la exigencia es POR CARRIL y la declara la receta
// (`observationClosure.notConformEvidenceRequiredFor`): el jefe de
// tienda de facility debe fotos, el supervisor sólo el motivo. El cuerpo
// no sabe qué carril llama, así que ya no puede ser estructural: la
// ruta la hace cumplir con `assertNotConformConstancia` del engine
// (400 NOT_CONFORM_EVIDENCE_REQUIRED / NOT_CONFORM_REASON_REQUIRED). Sin
// la regla declarada, todo carril de cierre debe la constancia completa
// —postventa responde igual que antes—.
// Desde ADR-0141 la ruta también recibe la RESPUESTA AL RECHAZO, porque
// /conformity es la única puerta del propietario (conformityRouteOwns):
//
//   `acepta_rechazo` — «Estoy de acuerdo»: recorre la arista
//                      rechazada → rechazada_cerrada. Sin nota ni fotos.
//   `apela_rechazo`  — «Pedir segunda revisión»: recorre la arista
//                      rechazada → rechazo_apelado. La nota es
//                      OBLIGATORIA (el nodo la exige, requiresNote) y las
//                      fotos son opcionales. Una vez por ciclo
//                      (MAX_REJECTION_APPEALS_PER_CYCLE): la ruta cuenta
//                      los eventos `rejection_appealed` del ciclo actual.
//   `reabrir`        — ADR-0142 §4: reabre con UN toque una observación
//                      cerrada por falta de respuesta (`auto_approval`)
//                      mientras su ventana siga vigente
//                      (`reopenableUntil` del wire). Sin fotos ni nota:
//                      no hubo conformidad que contradecir. Recorre la
//                      misma arista que «No conforme» (resuelta →
//                      abierta), sube el ciclo y limpia el overlay. No
//                      cuenta como apelación. Fuera de la ventana, o
//                      sobre un cierre por conformidad, 409
//                      OBSERVATION_NOT_REOPENABLE.
export const findingConformityBody = z.discriminatedUnion("verdict", [
    z.object({ verdict: z.literal("conforme") }).strict(),
    z.object({ verdict: z.literal("reabrir") }).strict(),
    z.object({ verdict: z.literal("acepta_rechazo") }).strict(),
    z
        .object({
        verdict: z.literal("apela_rechazo"),
        /** El motivo de la apelación, escrito o dictado (la transcripción
         *  del grabador puede volcarse acá): es lo único que el jefe tiene
         *  para resolverla, así que el esquema ya lo exige. */
        note: z.string().trim().min(1).max(4000),
        /** Fotos opcionales (ADR-0141 §1). */
        photoUrls: z.array(z.string().url()).max(8).default([]),
    })
        .strict(),
    z
        .object({
        verdict: z.literal("no_conforme"),
        photoUrls: z.array(z.string().url()).max(8).default([]),
        /** La nota de voz, que en este producto ES su transcripción: el
         *  grabador transcribe en el dispositivo y NADA sube el audio a
         *  ninguna parte —no hay un solo writer de evidencia `audio` en el
         *  cliente—, así que pedir una URL sería pedir algo que nadie puede
         *  producir. Coherente además con la regla dura 8: lo que se
         *  muestra es siempre la transcripción, nunca un reproductor.
         *  Obligatoria para el carril que debe la constancia completa
         *  (ADR-0134 §5); para el resto es una de las dos formas del
         *  motivo. La ruta lo hace cumplir, no el esquema. */
        transcript: z.string().trim().min(1).max(4000).optional(),
        /** Comentario escrito, opcional y aditivo. */
        note: z.string().trim().min(1).max(4000).optional(),
    })
        .strict(),
]);
export const findingConformityResponse = z.object({
    id: z.string().uuid(),
    observationStatus: z.string().nullable(),
    observationStatusAt: z.string().datetime().nullable(),
    observationClosedAt: z.string().datetime().nullable(),
    observationClosedReason: observationClosedReasonSchema.nullable(),
    observationCycle: z.number().int().positive().nullable(),
});
// POST /findings/:id/visit-response (ADR-0161, #0768) — el propietario
// confirma la visita que propuso el contratista o pide otro horario. Una
// sola respuesta por GRUPO (misma unidad, contratista, día y rango):
// `proposalIds` son las propuestas vigentes de todas sus observaciones, y
// el servidor exige que el conjunto sea exactamente el del grupo bajo el
// lock (409 VISIT_PROPOSAL_CHANGED si no). «Otro horario» es estructurado
// (decisión 3 del founder): días + una franja, sin texto libre — no existe
// `note`.
/** ADR-0161 — cuántas observaciones puede tener UNA visita que responde el
 *  propietario. El grupo junta envíos distintos del contratista (no lo
 *  acota `alsoItemIds`), así que el tope vive aquí y la proyección no
 *  ofrece responder un grupo más grande (lo coordina el jefe). Es también
 *  lo que lista el correo de la propuesta. */
export const OWNER_VISIT_GROUP_MAX = 100;
const visitResponseProposalIds = z
    .array(z.string().uuid())
    .min(1)
    .max(OWNER_VISIT_GROUP_MAX)
    .refine((ids) => new Set(ids).size === ids.length, { message: "proposalIds must be unique" });
export const findingVisitResponseBody = z.discriminatedUnion("response", [
    z.object({ response: z.literal("confirm"), proposalIds: visitResponseProposalIds }).strict(),
    z
        .object({
        response: z.literal("other_time"),
        proposalIds: visitResponseProposalIds,
        preferredDates: z
            .array(observationVisitDateSchema)
            .min(1)
            .max(OBSERVATION_VISIT_PREFERRED_DAYS_MAX)
            .refine((d) => new Set(d).size === d.length, {
            message: "preferredDates must be unique",
        }),
        preferredSlot: z.enum(OBSERVATION_VISIT_SLOTS),
    })
        .strict(),
]);
export const findingVisitResponseResponse = z.object({
    /** false = la misma respuesta ya estaba registrada (reintento). */
    changed: z.boolean(),
    confirmation: z.enum(OBSERVATION_VISIT_CONFIRMATIONS),
    findingIds: z.array(z.string().uuid()),
    /** La visita confirmada; null cuando se pidió otro horario. */
    visit: z
        .object({
        date: observationVisitDateSchema,
        startTime: observationVisitTimeSchema,
        endTime: observationVisitTimeSchema,
    })
        .nullable(),
});
// POST /findings/:id/classification (ADR-0129) — human correction of
// the postventa taxonomy classification: the supervisor validating /
// assigning, per spec §3 ("la asignación ES la clasificación de
// especialidad"). Deliberately ABSENT from this body, forever:
//   - severity / derivedSeverity — pure function of the booleans, no
//     direct write path (spec invariant 2);
//   - taxonomyVersion — server-stamped from the catalog constant;
//   - aiConfidence / ai* baseline fields — written once by the
//     classifier pipeline, never by a client;
//   - matrix anything — the matrix ranks, never blocks (invariant 7),
//     so there is nothing matrix-shaped to send.
// Membership of every code against the row's catalog version is
// validated server-side (validateTaxonomyClassification).
export const reviewActionSchema = z.enum(["candidate_pick", "free_text", "correction"]);
/** 422 de POST /findings/:id/classification (ADR-0121): un `reviewAction`
 *  —el acto que estampa la revisión y levanta el gate— sobre una fila cuyos
 *  ejes estructurales seguirían NULL después del merge.
 *  `validateTaxonomyClassification` solo valida pertenencia cuando el código
 *  NO es null, así que un NULL pasaba limpio y el sello caía sobre una fila
 *  estructuralmente incompleta. Es el control PRIMARIO del cambio de
 *  predicado: su exhaustividad la garantiza la fitness
 *  `review-stamp-requires-review-action`, que fija un único escritor de
 *  `confianza_review_at`. */
export const CLASSIFICATION_AXES_REQUIRED = "CLASSIFICATION_AXES_REQUIRED";
/** Los ejes que viajan en `details.ejes` del 422. Códigos, jamás copy: son
 *  miembros del vocabulario `falta` del engine, de modo que el dashboard
 *  renderiza las etiquetas que ya existen —FALTA_LABEL en core-api— en vez
 *  de un error opaco.
 *
 *  #0646 — la lista vivía acá como copia a mano guardada por un
 *  `satisfies ReadonlyArray<ConfianzaFaltaCode>`. `satisfies` verifica
 *  PERTENENCIA, no COBERTURA: un cuarto eje estructural en el engine
 *  dejaba esta copia atrás en silencio y en verde. Ahora se importa; se
 *  re-exporta como VALOR (no sólo como tipo) porque apps/web-dashboard la
 *  consume desde acá y no toma una arista contra core-engine — el mismo
 *  motivo que documenta el re-export de CLIENT_SETTABLE_DELEGATION_STATUSES
 *  al pie de este archivo. */
export { CLASSIFICATION_AXIS_CODES };
/**
 * #0646 — el ÚNICO puente entre los tres vocabularios que hablan de los
 * mismos tres ejes con nombres distintos: el código del eje
 * (`CLASSIFICATION_AXIS_CODES`, el que ya viaja en `falta` y en
 * `details.ejes`), el campo del body de /classification que lo
 * transporta, y la llave de `FuenteEjes` con que el modelo declara su
 * procedencia.
 *
 * `satisfies Record<ClassificationAxisCode, …>` da COBERTURA, no
 * pertenencia: agregar un cuarto eje estructural al engine rompe la
 * compilación acá en vez de dejar una fila muda. Es la misma lección que
 * obligó a mover `CLASSIFICATION_AXIS_CODES` al engine, aplicada al
 * mapeo en vez de a la lista.
 */
export const CLASSIFICATION_AXIS_WIRING = {
    recinto: { bodyField: "codRecinto", fuenteKey: "recinto" },
    elemento: { bodyField: "codElemento", fuenteKey: "elemento" },
    tipo_falla: { bodyField: "codTipoFalla", fuenteKey: "tipoFalla" },
};
/** 400 de POST /findings/:id/classification (#0646): el cliente afirmó una
 *  fuente por eje que el registro del servidor no respalda — un
 *  `confirmado_ia` sobre un código que ninguna corrida propuso, sobre un
 *  eje que el modelo marcó `inferido`, o sobre una columna que ya traía
 *  una decisión. El servidor no le cree la afirmación al cliente: la
 *  contrasta. Es 400 y no 422 por la misma línea divisoria que documenta
 *  el 422 de arriba — «lo que mandaste está mal», no «te falta algo». */
export const CLASSIFICATION_EJE_FUENTE_UNSUPPORTED = "CLASSIFICATION_EJE_FUENTE_UNSUPPORTED";
/** Mapa eje → fuente, con las llaves DERIVADAS de
 *  `CLASSIFICATION_AXIS_CODES` en vez de escritas a mano: un cuarto eje
 *  aparece acá solo, y no queda un eje sin forma de declarar su fuente.
 *  Parcial a propósito — un request declara únicamente los ejes que llena. */
const ejeFuenteMapShape = Object.fromEntries(CLASSIFICATION_AXIS_CODES.map((eje) => [eje, ejeFuenteSchema.optional()]));
export const ejeFuenteMapSchema = z.object(ejeFuenteMapShape).strict();
export const findingClassificationBody = z
    .object({
    codEspecialidad: z.string().min(1).max(8).optional(),
    codPartida: z.string().min(1).max(16).optional(),
    codTipoFalla: z.string().min(1).max(16).optional(),
    codRecinto: z.string().min(1).max(16).optional(),
    codElemento: z.string().min(1).max(16).optional(),
    codProcedencia: z.string().min(1).max(16).optional(),
    aguaActiva: z.boolean().nullable().optional(),
    anchoMm: z.number().positive().nullable().optional(),
    categoriaProvisional: z.boolean().optional(),
    p1Risk: z.boolean().optional(),
    p2FunctionOrProgression: z.boolean().optional(),
    p3MinorFunctional: z.boolean().optional(),
    note: z.string().max(2000).optional(),
    // Discriminador explícito del acto de clasificación humana que
    // levanta el gate de confianza (handoff rule 4, ADR-0129 enmienda
    // 2026-09-01): candidate_pick (P9/D3/D5), free_text (P10 / «Dejar
    // como Otro»), correction (modal D4). Ausente = corrección legítima
    // que NO levanta el gate (procedencia, booleanos, carril
    // assigning_party). Se infiere del request, jamás del delta de
    // campos — un pick que coincide con la sugerencia de la IA es
    // clasificación humana con changes = {}.
    reviewAction: reviewActionSchema.optional(),
    // #0646 — quién responde por cada eje que ESTE request llena. No es
    // un campo de clasificación (no aporta ningún valor a la fila): es
    // metadato sobre los que sí vienen, así que queda fuera del
    // `.refine()` de abajo igual que `note` y `reviewAction` — un body
    // que trajera sólo esto no clasifica nada.
    //
    // No se llama `procedencia` ni `ejeProcedencia`: ese nombre es de
    // `cod_procedencia`, el eje que ninguna máquina escribe
    // (fitness:no-automated-writer-on-procedencia). Ver EJE_FUENTE_CODES
    // en el engine.
    //
    // El servidor NO le cree al cliente: para `confirmado_ia` contrasta
    // el código contra la `proposal` de `finding_classification_runs` de
    // ESTE hallazgo, en la misma transacción.
    ejeFuente: ejeFuenteMapSchema.optional(),
})
    .strict()
    .refine((b) => Object.entries(b).some(([k, v]) => k !== "note" && k !== "reviewAction" && k !== "ejeFuente" && v !== undefined), { message: "at least one classification field is required" });
export const findingClassificationResponse = z.object({
    id: z.string().uuid(),
    taxonomyVersion: z.string(),
    codEspecialidad: z.string().nullable(),
    codPartida: z.string().nullable(),
    codTipoFalla: z.string().nullable(),
    codRecinto: z.string().nullable(),
    codElemento: z.string().nullable(),
    codProcedencia: z.string().nullable(),
    categoriaProvisional: z.boolean().nullable(),
    aguaActiva: z.boolean().nullable(),
    anchoMm: z.number().nullable(),
    p1Risk: z.boolean().nullable(),
    p2FunctionOrProgression: z.boolean().nullable(),
    p3MinorFunctional: z.boolean().nullable(),
    /** Derivada server-side — presente sii los tres booleanos lo están.
     *  Derivada del engine (`DERIVED_SEVERITIES`), no re-tipeada: la copia
     *  a mano vivía acá y en `findingDetailResponse`, y el enum del engine
     *  no estaba vigilado, así que las dos pasaban en verde. */
    derivedSeverity: z.enum(DERIVED_SEVERITIES).nullable(),
    /** Regla dura aplicada (spec invariante 3), para que la UI explique
     *  "P1 forzado por GAS" en vez de un booleano que 'no se deja' bajar. */
    p1ForcedByRule: z.enum(["GAS", "ELE_TF04"]).nullable(),
});
// ── Reclassify lane (ADR-0119 §2, PR-3) ─────────────────────────────
/** A candidate the human may pick from, already RESOLVED against the
 *  row's catalog version: code + name + one-line help. The catalog never
 *  crosses the wire; core-api translates ≤3 codes and ships the cards.
 *  Shared by GET /findings/:id (`aiCandidates`) and /reclassify. */
export const candidateCardSchema = z.object({
    codEspecialidad: z.string().min(1).max(8),
    nombre: z.string().min(1),
    ayuda: z.string().min(1),
    confianza: z.number().min(0).max(1),
});
/** Why the evidence clamp did or did not lift — explanation for
 *  P9/P10/D3, orthogonal to `motivo`. Code derived from the engine,
 *  label rendered server-side (ATRIBUCION_LABEL, single source). */
export const reclassifyAtribucionSchema = z.object({
    code: z.enum(RECLASSIFY_ATRIBUCION_CODES),
    label: z.string().min(1),
});
/** POST /findings/:id/reclassify body — what the supervisor confirmed in
 *  the field (P5/P6), plus an optional free-text `supervisorNote` (ai-ml-
 *  engineer review, contract widening) for a reclassify whose only new
 *  evidence is a dictated voice note rather than a photo or a differing
 *  structured hint. May be empty: a reclassify whose only new
 *  information is a freshly attached `detalle` photo sends `{}`. A
 *  reclassify with neither new evidence, nor a hint that differs from the
 *  live row, nor a `supervisorNote` is refused (409
 *  RECLASSIFY_REQUIRES_NEW_EVIDENCE). `supervisorNote` is distinct from
 *  `findingEvidenceBody`'s evidence-row `transcript`: this is per-request
 *  context for the reclassify call, not evidence attached beforehand. It
 *  never feeds the confidence clamp or `evidenciaVerificada` — those stay
 *  keyed on `verifiedPhotoIndices` alone. */
export const findingReclassifyBody = taxonomyHintsSchema.extend({
    supervisorNote: z.string().trim().max(4000).optional(),
});
/** #0646 — lo que el modelo PROPUSO en esta corrida para los tres ejes de
 *  taxonomía, se haya persistido o no.
 *
 *  Existe porque la fase 3 de /reclassify escribe la familia LIVE
 *  completa sólo en la rama `classified`, que exige una foto de detalle
 *  nueva citada. Sin foto nueva, una propuesta correcta —un supervisor
 *  dicta «es en la sala de estar», el modelo devuelve RC-02— se computaba
 *  y se descartaba, y al supervisor se le seguía pidiendo el recinto que
 *  el sistema ya había resuelto (evidencia real: PR 54, 2026-09-05
 *  20:25Z).
 *
 *  NO es estado: para saber qué quedó escrito hay que mirar `finding`.
 *  Compararlos es justamente lo que permite ofrecerle al supervisor
 *  confirmar un valor que la corrida no pudo persistir sola. Códigos
 *  crudos, no pares resueltos: el cliente ya trae `opciones` de
 *  GET /findings/:id y los resuelve con `nameFor`. */
export const reclassifyEjesPropuestosSchema = z.object({
    codRecinto: z.string().nullable(),
    codElemento: z.string().nullable(),
    codTipoFalla: z.string().nullable(),
    /** #0646 — la procedencia VERIFICADA que el sanitizador dejó sobre la
     *  propuesta: `declarado` cuando las palabras del supervisor alcanzan
     *  por sí solas para elegir una sola fila del catálogo, `inferido`
     *  cuando salió de la foto o de un desempate. Sin esto el cliente no
     *  puede saber si le corresponde ofrecer el tap de confirmación, y
     *  ofrecerlo sobre un eje `inferido` es exactamente el «clic de
     *  trámite» que ADR-0119 §Context prohíbe.
     *
     *  Va DENTRO de este objeto y no como hermano a propósito: es una
     *  afirmación sobre estos tres códigos, y separarla dejaría al cliente
     *  leer un código sin su procedencia. Opcional en el wire por la misma
     *  razón que en el engine — durante un despliegue parcial llega
     *  ausente y un cliente que exija `"declarado"` falla CERRADO. */
    fuenteEjes: z
        .object({
        recinto: z.enum(FUENTES_EJE).nullable(),
        elemento: z.enum(FUENTES_EJE).nullable(),
        tipoFalla: z.enum(FUENTES_EJE).nullable(),
    })
        .optional(),
    /** El fragmento literal del habla que sustenta el recinto, ya
     *  verificado server-side como substring normalizado del texto que el
     *  modelo efectivamente recibió. Se le muestra al supervisor para que
     *  ratifique el MAPEO LÉXICO («dijiste "la sala de estar" → Living -
     *  comedor») en vez de toda la cadena voz→código.
     *
     *  `citaRecintoOrigen` NO cruza al wire: es discriminador de servidor
     *  (¿nota base o voz de este remedio?) y no cambia nada de lo que el
     *  cliente puede hacer. */
    citaRecinto: z.string().nullable().optional(),
    /** #0646-ext — el conjunto de recintos que la evidencia no permitió
     *  descartar, cuando el modelo NO pudo elegir una fila. Es el camino
     *  del caso sin salida: una foto de un baño sin nota dictada deja
     *  `codRecinto` en null con criterio (el catálogo distingue baño
     *  principal / secundario / de visitas y la foto no los desambigua),
     *  el acta queda retenida y el supervisor no tiene nada que tocar. Con
     *  el conjunto la pantalla muestra las 2-3 filas y él ELIGE.
     *
     *  Excluyente con `codRecinto`: viene vacío o ausente cuando el eje ya
     *  quedó resuelto, porque un picker sobre un eje resuelto es lo que la
     *  Guarda 2 de ADR-0122 §6 rechaza server-side (`eje_ya_resuelto`).
     *
     *  Códigos CRUDOS y no pares resueltos, igual que los tres de arriba:
     *  el cliente ya trae `opciones` de GET /findings/:id y los resuelve
     *  con `nameFor`, así que no se ensancha la superficie que vigila
     *  fitness:taxonomy-catalog-never-on-the-wire. El `.max(3)` es
     *  ESTRUCTURAL y no confianza en el sanitizador: un tope sostenido por
     *  disciplina es un tope que la próxima ruta olvida. El ORDEN es el
     *  ranking del modelo y es el orden de la pantalla. */
    candidatosRecinto: z.array(z.string()).max(3).optional(),
    /** Procedencia del CONJUNTO. Cambia el copy, no la mecánica: con
     *  `declarado` la pantalla le cita al supervisor sus propias palabras
     *  («Dijiste "en el baño" — ¿cuál?»), con `inferido` no («parece un
     *  baño — ¿cuál?»). Ratificar el propio dicho y elegir sobre una
     *  corazonada de la máquina no son el mismo acto. Opcional por la
     *  misma razón que `fuenteEjes`: en un despliegue parcial llega
     *  ausente y el cliente falla CERRADO. */
    fuenteCandidatosRecinto: z.enum(FUENTES_EJE).nullable().optional(),
});
const reclassifyBase = z.object({
    /** The float written to ai_confidence by this run — or `null` on the
     *  unattributed branch, where the number would be the clamp ceiling,
     *  not a measurement, and no client may render it as one. */
    confianza: z.number().min(0).max(1).nullable(),
    atribucion: reclassifyAtribucionSchema,
    /** Live state AFTER this run's write, on every branch — phase 3 writes
     *  live columns on all three, and the client stays on screen in P9/P10
     *  precisely where it would otherwise be stale. */
    finding: findingClassificationResponse,
    /** La propuesta de esta corrida — ver `reclassifyEjesPropuestosSchema`.
     *  Va en las tres ramas: en `classified` coincide con lo que `finding`
     *  ya trae, y en las dos sub-umbral es lo que NO se escribió. Uniforme a
     *  propósito, para que el cliente pueda diffear siempre contra
     *  `finding` en vez de ramificar. */
    ejesPropuestos: reclassifyEjesPropuestosSchema,
    /** #0646 — el estado del gate DESPUÉS de esta corrida, en las tres
     *  ramas (misma disciplina que `ejesPropuestos`: el cliente diffea,
     *  nunca ramifica).
     *
     *  Costo de cómputo cero: el handler ya construye la `ConfianzaRowView`
     *  post-escritura y ya calcula los dos valores para la fila de
     *  auditoría de #0633 — hasta ahora los tiraba. Sin este campo el
     *  cliente tendría que re-derivar `revisionPendiente` en el navegador
     *  (un sexto gemelo del predicado, en otro lenguaje, fuera del alcance
     *  del drift-throw que vigila a los otros cinco) o pagar un
     *  `GET /findings/:id` extra en terreno.
     *
     *  `falta` es el código a secas, sin etiqueta: el cliente que necesita
     *  copy la trae del `falta` de GET /findings/:id, que sí la lleva. */
    gate: z.object({
        retenida: z.boolean(),
        falta: confianzaFaltaCodeSchema.nullable(),
    }),
});
/** 200 of POST /findings/:id/reclassify. The SERVER decides the branch;
 *  the client only switches on `status`. */
export const findingReclassifyResponse = z.discriminatedUnion("status", [
    // P8 — confianza ≥ 0.75. Attribution is `verificada` by construction:
    // the clamp caps unattributed runs at 0.70.
    reclassifyBase.extend({ status: z.literal("classified") }),
    // P9 — under threshold, ≥2 candidates.
    reclassifyBase.extend({
        status: z.literal("candidates"),
        candidatos: z.array(candidateCardSchema).min(2).max(3),
    }),
    // P10 — under threshold, 0–1 candidates. Straight to free text.
    reclassifyBase.extend({
        status: z.literal("free_text_required"),
        motivo: z.enum(["sin_candidatos", "un_solo_candidato"]),
        candidato: candidateCardSchema.nullable(),
    }),
]);
/** POST /findings/:id/evidence — attach an already-uploaded photo (or,
 *  since the audio widening, voice note) as evidence before
 *  reclassifying. `captureRole` is REQUIRED: this is the route that
 *  finally writes `evidence.capture_role` (no writer existed before
 *  it), and "Falta foto de detalle" is only computable from it —
 *  required only in the sense of "the client must send it"; the route
 *  forces it to `null` server-side for `evidenceType: "audio"`
 *  regardless of what's sent (evidence_capture_role_photo_only_check,
 *  apps/core-api/src/db/schema.ts), so the client value is honored only
 *  for photo rows. `evidenceType` defaults server-side to "photo" when
 *  omitted (backward compat with every caller predating the audio
 *  widening) — derived from the engine's evidenceTypeSchema (invariant
 *  7), not a hand-rolled literal. `transcript` is optional and only
 *  meaningful for audio rows; length cap mirrors evidenceSchema.caption
 *  (packages/core-engine/src/entities/evidence.ts) and the DB's
 *  evidence_caption_length_check convention (max 4000). */
export const findingEvidenceBody = z
    .object({
    url: z.string().url(),
    captureRole: z.enum(EVIDENCE_CAPTURE_ROLES),
    caption: z.string().trim().max(500).nullable().optional(),
    evidenceType: evidenceTypeSchema.optional(),
    transcript: z.string().trim().min(1).max(4_000).nullable().optional(),
})
    .strict();
// GET /findings/:id (ADR-0085) — a single-finding detail read for the
// gerencial dashboard's "Observación · hilo" screen. Product-core, not a
// dashboard-namespaced route (architecture-engineer's ruling): /observaciones
// is capability-dispatched across facility and postventa via two different
// list endpoints, and /facility-dashboard 403s postventa orgs entirely, so a
// facility-namespaced detail route would be structurally unreachable for
// half its callers. Keyed on findingId alone — loadScopedFinding already
// supplies the authorization scoping, and a composite (assetId, findingId)
// path only invents an ambiguous mismatch case with no good answer.
// Breadcrumb context, mirroring the five asset fields facilityDashboardEntry
// already denormalizes so the two agree without a second round trip.
export const findingDetailAssetSchema = z.object({
    id: z.string().uuid(),
    unit: z.string().nullable(),
    address: z.string().nullable(),
    assetGroupId: z.string().uuid().nullable(),
    assetGroupName: z.string().nullable(),
    brandId: z.string().uuid().nullable(),
    brandName: z.string().nullable(),
    /** Propietario de la unidad, mismo origen que `ownerLabel` en la
     *  lista de unidades de la cartera: `assets.attributes[
     *  recipe.labels.assetInfoFieldKey]`. Null cuando la receta no declara
     *  la clave o la unidad no la tiene. Opcional por compatibilidad. */
    ownerLabel: z.string().nullable().optional(),
    /** ADR-0150 — el contacto para visitas vigente de la unidad, para que
     *  el jefe coordine («Arrendada · coordinar con {nombre} {teléfono}»).
     *  null = sin contacto; ausente sin canal del propietario (facility). */
    visitContact: unitVisitContactSchema.nullable().optional(),
});
// Derived from the engine's evidenceSchema via .pick() — the deny-list is
// structural, not a discipline: objectKey never reaches the wire because it
// is never picked, not because someone remembered to omit it. authorType
// is optional on the engine schema (mirrors the DB column's write-time
// DEFAULT 'ai', so callers constructing a new row don't have to know
// that) but the route that populates this response always selects the
// real column value straight off an existing row, so .required() narrows
// it back — still derived from the engine's enum, not a second
// hand-declared z.enum(["ai","user","provider"]) literal.
export const findingDetailEvidenceSchema = evidenceSchema
    .pick({
    id: true,
    evidenceType: true,
    url: true,
    caption: true,
    createdAt: true,
    authorType: true,
    // #0454 — additive only: timeline[] keeps its ADR-0085 shape; the
    // FE joins evidence→event client-side on this nullable id.
    findingEventId: true,
})
    .required({ authorType: true })
    .extend({
    // The engine schema says `.optional()` (a caller constructing a row
    // may omit it) but the DB column is nullable and the detail route
    // normalizes to `?? null` — the wire must accept what the route
    // actually sends. Derived from the engine's own field (same max),
    // widened with null; a bare re-pick 400'd every real-data detail
    // read whose evidence carried no caption (PR-54 preview incident,
    // latent since ADR-0085 — fixtures always set captions).
    caption: evidenceSchema.shape.caption.nullable(),
    // ADR-0119 PR-3 — which shot this is (`contexto` | `detalle`), or
    // null for every row captured before the gate (no backfill, by
    // decision). P4's checklist derives «Foto de contexto ✓ / Foto de
    // detalle pendiente» from this; without it the client would have to
    // guess from ordering. Written only by POST /findings/:id/evidence.
    // Derived from the engine's EVIDENCE_CAPTURE_ROLES (invariant 7 drift
    // fix), not a second hand-declared enum literal.
    captureRole: z.enum(EVIDENCE_CAPTURE_ROLES).nullable(),
});
// Actor identity without crossing the auth-svc boundary (ADR-0085, Part 2 —
// Option C). The dashboard resolves userId -> displayName client-side
// against its own cached GET /auth/memberships roster; core-api never
// calls auth-svc on this read path and finding_events never stores a name.
// via_share_link_id itself never crosses the wire — it is a capability
// handle in the share-link portal design, not a display value.
//
// `self` y `organization` son la PROYECCIÓN POR AUDIENCIA del mismo
// evento. Un lector de carril `asset_party` —el propietario de una unidad
// de postventa— no tiene roster contra el cual resolver un `userId`, y no
// debe tenerlo: no es empleado del tenant. Para él, el servidor proyecta
// todo actor humano a `self` (cuando es él mismo, la fuente del chip
// «Tú») o a `organization` con el nombre de la inmobiliaria, que es la
// firma que el spec exige hacia afuera — la persona concreta nunca sale.
//
// La asimetría con `user` es deliberada y sigue la misma línea que la de
// `provider` más abajo: el nombre de la organización es una columna que
// core-api posee, así que proyectarlo es un join, no una llamada entre
// servicios. Lo que no se puede resolver server-side es el nombre de una
// PERSONA, y por eso el carril de operador sigue recibiendo `user` crudo
// y resolviéndolo como siempre.
export const findingTimelineActorSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("user"), userId: z.string().uuid() }),
    z.object({ kind: z.literal("self") }),
    z.object({ kind: z.literal("organization"), name: z.string().min(1) }),
    z.object({ kind: z.literal("share_link") }),
    z.object({ kind: z.literal("system") }),
]);
// Ordering contract (ADR-0085, Part 1): ascending by (occurredAt, seq).
// `seq` is opaque to clients — it exists solely to make the order total,
// since created_at alone ties within a transaction (the /delegate
// reassignment path writes "released" and "assigned" with an identical
// timestamp) and on seeded/back-dated data. Do not sort this array by
// occurredAt alone.
export const findingTimelineEntrySchema = z.object({
    id: z.string().uuid(),
    occurredAt: z.string().datetime(),
    seq: z.number().int(),
    // Open string, not a closed enum: the event-type vocabulary is spread
    // across a schema comment and an engine helper that already disagree by
    // one value (ADR-0085) — a closed wire enum would reject a real event
    // type client-side. eventType is the sole discriminator between the two
    // vocabularies a single column carries; never recipe.slug.
    eventType: z.string(),
    fromStatus: z.string().nullable(),
    toStatus: z.string().nullable(),
    actor: findingTimelineActorSchema,
    // providers is core-api-owned, so denormalizing here is a plain join,
    // not a cross-service concern — the asymmetry with actor above is the
    // point: provider identity is free, user identity is not, and the line
    // is table ownership, not display convenience.
    provider: z.object({ id: z.string().uuid(), shortName: z.string() }).nullable(),
    // La evidencia y la transcripción de un hito NO se embeben acá: el
    // hito las reclama por el join `evidence.findingEventId` que
    // `findingDetailEvidenceSchema` ya documenta (#0454). Los tres diseños
    // del hilo las pintan DENTRO del evento, y eso es composición del
    // cliente sobre este join — duplicarlas en el entry sería una segunda
    // representación del mismo hecho, con el riesgo clásico de que una de
    // las dos se quede atrás.
    //
    // La transcripción por hito hoy NO TIENE ESCRITOR: en producción nada
    // crea evidencia de tipo `audio` ni `note` (2026-09-23; sólo lo hacen
    // fixtures de test), y la única transcripción viva es
    // `findings.audioTranscript`, que es la del nacimiento de la
    // observación y por eso cuelga del primer hito. Cuando F8
    // (/conformity) y F9 (evidencia del contratista) empiecen a escribir
    // voz, la transcripción viaja en la fila de evidencia —`caption` sobre
    // una fila `audio`, colgada de su `findingEventId`— y no como un campo
    // nuevo de este schema.
    note: z.string().nullable(),
    // ── S2 ──
    /** Desde qué carril se escribió el hito, resuelto server-side desde la
     *  procedencia del evento: `viaShareLinkId` → `share_link`; los hitos
     *  del propietario (conformidad / no conformidad, intake del
     *  propietario) → `asset_party`; un actor humano del tenant → el
     *  carril de personal de la receta; sin actor → `system`. Opcional
     *  porque `assetObservationDetailResponse` comparte este entry y el
     *  carril del propietario no lo necesita. */
    actorLane: timelineActorLaneSchema.nullable().optional(),
    /** `true` en el primer hito del hilo, «reported», que NO existe como
     *  fila en `finding_events`: se sintetiza desde `findings.createdAt`
     *  para que el hilo empiece donde la observación nació. Su `id` es el
     *  de la observación y su `seq` es -1 (siempre antes de cualquier
     *  fila real). El cliente no cruza evidencia por `findingEventId`
     *  contra él — la evidencia de nacimiento es la que no cuelga de
     *  ningún hito, y viaja resumida en `evidenceCount`. */
    synthetic: z.boolean().optional(),
    evidenceCount: z.number().int().nonnegative().optional(),
});
// Six fields are structurally excluded here, not by discipline:
// evidence.objectKey (never picked, see findingDetailEvidenceSchema),
// evidence/finding_events.viaShareLinkId (capability handle, not a display
// value), finding_events.toStatusTerminal ("CONSTRAINT INPUT ONLY — never a
// read source", schema.ts), providers.contactEmail/phone (dispatch-purpose
// fields, not detail-view fields), reports.findingsSnapshot (a live read
// must never expose the frozen artifact it coexists with).
export const findingDetailResponse = z.object({
    // `recinto` también se omite, y por la misma clase de razón que las
    // seis de arriba: esta respuesta ya trae `roomCode` crudo y `opciones`
    // (las pick-lists resueltas a la versión de la fila), y el cliente
    // supervisor los cruza con `nameFor` — igual que hace con
    // `ejesPropuestos` de /reclassify (ADR-0122 §5). Sumar acá la
    // proyección resuelta del feed sería una segunda representación del
    // mismo hecho en la misma respuesta: deriva, no reuso.
    observation: assetObservationSchema.omit({ evidenceUrls: true, recinto: true }).extend({
        assetId: z.string().uuid(),
        // ADR-0067 — when the observation was made, when that differs from
        // when the row was written. Same coalesce(observedAt, createdAt)
        // convention GET /facility-dashboard's age sort already uses.
        observedAt: z.string().datetime().nullable(),
        providerId: z.string().uuid().nullable(),
        provider: z.object({ id: z.string().uuid(), shortName: z.string() }).nullable(),
        // Engine-derived, unlike observationStatus above (inherited loose from
        // assetObservationSchema, recipe-declared vocabulary) — the asymmetry
        // is the boundary this schema exists to preserve, not drift.
        delegationStatus: delegationStatusSchema.nullable(),
        delegatedAt: z.string().datetime().nullable(),
        resolvedAt: z.string().datetime().nullable(),
        // True only when reports.findingsSnapshot is an array (ADR-0058) —
        // never derived from signedAt IS NOT NULL, which is also true for
        // pre-ADR-0058 signed reports that carry no snapshot and whose acta
        // renders live. A signedAt-derived flag would be false for that
        // cohort, asserting a legal property the artifact doesn't have.
        sourceFrozen: z.boolean(),
        // ADR-0119 PR-3 — the route always emitted this; the schema stripped
        // it, so no client could show «Folio 12 · Falta foto de detalle»,
        // the handoff's canonical example of its own guiding principle.
        folio: z.string().nullable(),
        // ADR-0129 — the taxonomy classification, on the SUPERVISOR surface
        // only (this authenticated detail read): these are the RAW columns,
        // and the assign modal reads the ai* baseline to render the IA
        // suggestion next to the human pick.
        //
        // Este comentario decía «the owner-facing wire never carries these
        // codes (spec invariant 8)». F5 (carril del propietario) rompe esa
        // lectura POR CONSTRUCCIÓN: `opciones`, `ejes` y `fijados` de
        // `POST /assets/:id/observations/classify` llevan códigos de ida y de
        // vuelta, porque el cliente tiene que devolver lo que el humano
        // eligió. La enmienda es ADR-0127: el wire puede llevar códigos
        // ÚNICAMENTE como identificadores de ida y vuelta — el cliente recibe
        // pares `{codigo, nombre}`, renderiza `nombre` y devuelve `codigo`;
        // ningún `codigo` se renderiza jamás. La prohibición absoluta se
        // mantiene, pero a nivel de UI, no de wire. Lo que sigue fuera del
        // alcance del propietario es el CATÁLOGO (fitness
        // taxonomy-catalog-never-on-the-wire) y estas columnas crudas, que no
        // son pares resueltos y no tienen nombre que renderizar.
        specialtyCode: z.string().nullable(),
        itemCode: z.string().nullable(),
        failureTypeCode: z.string().nullable(),
        roomCode: z.string().nullable(),
        elementCode: z.string().nullable(),
        originCode: z.string().nullable(),
        categoryProvisional: z.boolean().nullable(),
        derivedSeverity: z.enum(DERIVED_SEVERITIES).nullable(),
        taxonomyVersion: z.string().nullable(),
        aiSuggestedSpecialtyCode: z.string().nullable(),
        aiSuggestedItemCode: z.string().nullable(),
        // Gate de confianza (handoff 2026-09-01, D3/D5/D6). All optional so
        // existing fixtures keep compiling; the client consumes the booleans
        // by name and never re-derives the 0.75 threshold. Suppressed for
        // the asset_party reader server-side.
        aiConfidence: z.number().min(0).max(1).nullable().optional(),
        // Resolved server-side by ONE resolver (lib/taxonomy-candidates.ts)
        // from `classification_candidates` + the row's catalog version —
        // never the catalog itself on the wire. `.max(3)` is structural:
        // the cap the fitness `taxonomy-catalog-never-on-the-wire` commits
        // to must not depend on resolver discipline.
        aiCandidates: z.array(candidateCardSchema).max(3).nullable().optional(),
        // Null when the row has no taxonomy version (never classified) or
        // its version is unknown to the registry — the client renders the
        // pickers unavailable instead of guessing from a stale list.
        opciones: taxonomyOptionsSchema.nullable().optional(),
        revisionPendiente: z.boolean().nullable().optional(),
        // #0646 — era `z.object({ code: z.string(), label: z.string() })`,
        // una copia a mano del par que reports.ts ya exporta y que ya
        // nombraba a este read model como consumidor. El string libre
        // aplanaba el enum justo en el campo con el que el cliente decide
        // qué pantalla de remedio abrir.
        falta: confianzaFaltaSchema.nullable().optional(),
        // ADR-0163 — derivado del vocabulario del engine (`INTAKE_CHANNELS`),
        // que suma `consulta_propietario`; un test lo amarra.
        origen: z.enum(INTAKE_CHANNELS).nullable().optional(),
        confianzaReviewAt: z.string().datetime().nullable().optional(),
        confianzaReviewBy: z.string().nullable().optional(),
        // ── S2: read model derivado (ADR-0105 / ADR-0129), mismo contrato
        // que el feed del panel (`postventaObservationSchema`). Opcionales
        // por ADR-0021 D6. `derivedSeverity` (arriba) sigue siendo la
        // columna cruda; `effectiveSeverity` es la que el badge pinta.
        // `observationCycle`, `observationClosedAt`, `observationClosedReason`
        // y `reopenCount` se heredan de `assetObservationSchema` (todos
        // opcionales): el detalle y el feed comparten una sola declaración.
        observationPhase: z.enum(OBSERVATION_PHASES).nullable().optional(),
        reopened: z.boolean().nullable().optional(),
        /** ADR-0141 — apelación del rechazo en curso (modificador derivado,
         *  mismo patrón que `reopened`). */
        appealed: z.boolean().nullable().optional(),
        effectiveSeverity: z.enum(DERIVED_SEVERITIES).nullable().optional(),
        severityOrigin: severityOriginSchema.nullable().optional(),
        ownerDescriptions: z.array(ownerDescriptionSchema).optional(),
        /** ADR-0156 — la marca del aviso inmediato por observación crítica
         *  (misma forma que en el feed del panel). */
        criticalAlert: criticalAlertMarkSchema.nullable().optional(),
        /** ADR-0163 — la consulta del propietario de la que nació (canal
         *  `consulta_propietario`): el enlace a la bandeja y las fotos que el
         *  jefe eligió (ids de `owner_inquiry_attachments`, en su orden; las
         *  URL firmadas se piden a GET /owner-inquiries/:id/attachments/urls).
         *  Sólo para los roles que leen la bandeja; null en otro canal. */
        sourceInquiry: z
            .object({
            inquiryId: z.string().uuid(),
            attachmentIds: z.array(z.string().uuid()).max(10),
            convertedAt: z.string().datetime(),
            convertedByUserId: z.string().uuid(),
        })
            .nullable()
            .optional(),
    }),
    /** #0660 — el conjunto de recintos que la ÚLTIMA corrida del
     *  clasificador ofreció para esta observación, proyectado desde
     *  `finding_classification_runs.proposal`.
     *
     *  Existe porque el conjunto sólo viajaba en el 200 de `/reclassify`
     *  (`ejesPropuestos`), y la clasificación INICIAL no tenía transporte:
     *  una foto de un baño sin nota dictada dejaba `codRecinto` en null con
     *  criterio, el modelo enumeraba RC-05/RC-06/RC-07 —verificado en la
     *  base del PR 54, `intake_sync` 2026-09-06 12:42:21Z— y la pantalla
     *  decía «Falta el recinto» sin ofrecer nada. Lo que la nota de voz
     *  aportaba no era conocimiento: era un round-trip por `/reclassify`.
     *
     *  Hermano de `reclassifyEjesPropuestosSchema` pero deliberadamente
     *  MÁS ANGOSTO: sólo el conjunto, nunca los tres códigos propuestos.
     *  Con los códigos, un `codRecinto` `declarado` de una corrida vieja
     *  reabriría el carril de CONFIRMACIÓN (`recintoConfirmable`) en cada
     *  carga en frío de E3, que es un acto humano distinto —ratificar el
     *  propio dicho— y ya tiene su dueño en E6b. Este campo abre un solo
     *  carril: elegir.
     *
     *  Códigos CRUDOS, misma razón que allá: el cliente ya trae `opciones`
     *  y los resuelve con `nameFor`, así que el catálogo no se ensancha
     *  sobre el wire (fitness: taxonomy-catalog-never-on-the-wire).
     *
     *  `null` cuando no hay nada que elegir. `.optional()` además de
     *  `.nullable()` por la misma disciplina que `fuenteEjes`: contra un
     *  core-api anterior a este campo llega ausente y el cliente falla
     *  CERRADO. */
    eleccionRecinto: z
        .object({
        /** 2 o 3 códigos, en el orden del modelo, que es el orden de la
         *  pantalla. El piso de 2 y el techo de 3 son los de ADR-0122 §6 y
         *  se re-verifican server-side: un conjunto de 1 es la fila que el
         *  modelo se negó a escribir, y uno de 4+ es el picker que #0627
         *  retiró por fricción real. */
        candidatos: z.array(z.string()).min(2).max(3),
        /** Procedencia del CONJUNTO. Cambia el copy, no la mecánica: con
         *  `declarado` la pantalla cita las palabras del supervisor, con
         *  `inferido` no. Nunca null acá — el servidor omite el objeto
         *  entero cuando la fuente no es legible, en vez de mandar un
         *  conjunto que el cliente tendría que descartar igual. */
        fuente: z.enum(FUENTES_EJE),
        /** El fragmento literal ya verificado server-side, sólo con
         *  `declarado`. */
        cita: z.string().nullable(),
    })
        .nullable()
        .optional(),
    /** F8 (corte 6) — ¿se puede agendar la visita de ESTA observación desde
     *  este carril? Sólo con contraparte externa (postventa), con la
     *  observación en manos del contratista y para quien puede reasignarla.
     *  Ausente contra un core-api anterior: el panel no muestra la acción. */
    visitSchedulable: z.boolean().optional(),
    /** F8 — el último pedido de nueva visita del contratista que todavía no
     *  se atendió (posterior a la última asignación o fecha agendada): el
     *  panel lo muestra para que el jefe reagende. `reason` es la clave del
     *  enum; `note`, el detalle libre del contratista. Null sin pedido. */
    visitRequest: z
        .object({
        reason: z.enum(PUBLIC_VISIT_REASONS),
        note: z.string().nullable(),
        requestedAt: z.string().datetime(),
    })
        .nullable()
        .optional(),
    /** ADR-0160 — la visita vigente la propuso el contratista desde su
     *  enlace (sólo mientras está con él): cuándo, su estado («por
     *  confirmar»), si cae después del plazo y cuántas escrituras lleva su
     *  enlace. Null si la agendó el personal o no hay visita. */
    visitProposal: z
        .object({
        proposedBy: z.literal("contractor"),
        proposedAt: z.string().datetime(),
        confirmation: z.enum(OBSERVATION_VISIT_CONFIRMATIONS),
        afterDue: z.boolean(),
        changesUsed: z.number().int().min(0),
        // ADR-0161 — todos opcionales (un core-api anterior no los manda).
        proposalId: z.string().uuid().optional(),
        /** La hora límite del grupo, si la visita se confirmará sola; null
         *  si no (sin constancia, job apagado, la visita ya pasó…). */
        confirmBy: z.string().datetime().nullable().optional(),
        /** Cuándo respondió el propietario (o se confirmó sola). */
        respondedAt: z.string().datetime().nullable().optional(),
        /** Lo que pidió el propietario, sólo con `other_time_requested`. */
        ownerPreference: z
            .object({
            dates: z
                .array(observationVisitDateSchema)
                .min(1)
                .max(OBSERVATION_VISIT_PREFERRED_DAYS_MAX),
            slot: z.enum(OBSERVATION_VISIT_SLOTS),
        })
            .nullable()
            .optional(),
        /** El correo «Visita por confirmar» al propietario. `not_tracked` =
         *  una propuesta anterior a ADR-0161, sin constancia. */
        ownerNotice: z.enum(["sent", "pending", "failed", "no_recipient", "not_tracked"]).optional(),
        /** Por qué esta visita no se confirmará sola. */
        autoConfirmBlocked: z
            .enum([
            "no_notice",
            "notice_failed",
            // El correo salió sin hora límite (job apagado, o con menos de
            // 12 h de aviso): nunca se confirma sola.
            "no_deadline",
            "owner_changed",
            "link_inactive",
            "job_off",
        ])
            .nullable()
            .optional(),
        /** Las observaciones del mismo grupo (incluida ésta). */
        groupFindingIds: z.array(z.string().uuid()).optional(),
        /** ¿El enlace del contratista sigue vivo? (con `changesUsed`, si
         *  puede proponer otra). */
        contractorLinkActive: z.boolean().optional(),
    })
        .nullable()
        .optional(),
    /** ADR-0160 — las últimas fechas escritas en el ciclo actual, la más
     *  reciente primero, de quién vinieron y cuándo. Sin ids. La primera es
     *  la vigente SÓLO si sigue en pie: pedir otra visita o reasignar sin
     *  fecha la borran sin dejar fila, así que quien lea el historial la
     *  compara con `observation.visit`. Las visitas anteriores a la
     *  migración no aparecen. */
    visitHistory: z
        .array(z.object({
        date: observationVisitDateSchema,
        startTime: observationVisitTimeSchema,
        endTime: observationVisitTimeSchema,
        proposedBy: z.enum(OBSERVATION_VISIT_PROPOSERS),
        at: z.string().datetime(),
    }))
        .optional(),
    asset: findingDetailAssetSchema,
    evidence: z.array(findingDetailEvidenceSchema),
    timeline: z.array(findingTimelineEntrySchema),
});
// GET /assets/:id/observations/:findingId — el detalle de UNA observación
// por la puerta asset-scoped.
//
// Existe como ruta propia, y no como un ensanche de `GET /findings/:id`,
// por una razón de seguridad. Esa ruta está gateada por
// `isFindingManagementRole`, y ese predicado gobierna dos cosas a la vez:
// leer el hilo Y mover la delegación. Meter a `propietario` en ese set
// para que pueda ver su propia observación le entregaría de paso el
// carril de delegación — exactamente la frontera que F3 levantó al
// sacarlo de los cuatro sets de capacidad. La puerta correcta es la que
// ya usa para su lista: el alcance por activo.
//
// La forma es deliberadamente más angosta que `findingDetailResponse`:
// `observation` es el mismo `assetObservationSchema` del feed —que ya
// suprime folio y códigos para el carril `asset_party`— sin los campos
// crudos de taxonomía, sin `aiConfidence` y sin los candidatos que la IA
// barajó. Nada de eso es del propietario (regla dura 9 y §2.7 del spec).
//
// `timeline` es el mismo contrato de ADR-0085 —ascendente por
// `(occurredAt, seq)`, y el cliente invierte para pintar— con los actores
// ya proyectados por audiencia.
export const assetObservationDetailResponse = z.object({
    observation: assetObservationSchema,
    /** Las palabras con que el propietario reportó (transcripción dictada
     *  y/o `original_text`), mismo contrato que el panel: un texto
     *  REDACTADO viaja `{ text: null, redacted: true }` y nunca se rellena
     *  con `humanDescription`. Sólo las lleva una observación nacida por el
     *  canal del propietario (`reporte_propietario`, o desde ADR-0163 la
     *  consulta convertida, y entonces sólo para el AUTOR de la consulta:
     *  `humanDescription` ahí es el texto del jefe) en una receta con
     *  ciclo de observación: las palabras de otro autor no se le muestran
     *  como suyas, y facility conserva su respuesta. Vive en el envelope y
     *  no en `assetObservationSchema` para que el feed y el portal del
     *  contratista no la hereden. `.optional()`: contra un core-api
     *  anterior llega ausente. */
    ownerDescriptions: z.array(ownerDescriptionSchema).optional(),
    /** ADR-0163 — la observación nació de una consulta de ESTE lector
     *  (canal `consulta_propietario` y autor de la consulta = quien lee): el
     *  enlace «Ver la consulta» y las fotos que el jefe eligió (ids de
     *  adjuntos; las URL se piden a GET /assets/:id/inquiries/:inquiryId/
     *  attachments/urls). Un dueño posterior de la unidad recibe null: no ve
     *  las palabras, el enlace ni las fotos del anterior. Vive en el sobre,
     *  igual que `ownerDescriptions`, para que el portal del contratista no
     *  lo herede. */
    fromInquiry: z
        .object({
        inquiryId: z.string().uuid(),
        attachmentIds: z.array(z.string().uuid()).max(10),
    })
        .nullable()
        .optional(),
    evidence: z.array(findingDetailEvidenceSchema),
    timeline: z.array(findingTimelineEntrySchema),
    /** ADR-0148 — los ids de los hitos de `timeline` que son nuevos para
     *  este lector (la misma regla que `observation.newEventsCount`, de la
     *  misma consulta): el hilo pinta el separador «N nuevos» con esto y no
     *  re-deriva la regla. Sólo hacia la contraparte EXTERNA; opcional. */
    newEventIds: z.array(z.string().uuid()).optional(),
    /** ADR-0148 — el instante del servidor en que se armó esta respuesta.
     *  El cliente lo devuelve en `POST …/read` para marcar leídos los avisos
     *  de la campana de esta observación escritos HASTA ese instante (un
     *  aviso que llega después sigue sin leer). Nunca el reloj del cliente.
     *  Sólo hacia la contraparte EXTERNA; opcional. */
    servedAt: z.string().datetime().optional(),
});
/** POST /assets/:id/observations/:findingId/read (ADR-0148) — «hasta aquí
 *  leí» del hilo de una observación, sólo para la contraparte externa.
 *  - `lastReadAt`: el `occurredAt` del hito real (no el sintético) más
 *    reciente que se MOSTRÓ, o null si el hilo no tiene hitos reales. El
 *    servidor lo acota al último hito y sólo avanza la marca (GREATEST).
 *  - `servedAt`: el `servedAt` de la respuesta que se pintó; acota qué
 *    avisos de la campana de esta observación quedan leídos.
 *  204 sin cuerpo. Idempotente por construcción (sin Idempotency-Key). */
export const assetObservationReadRequestSchema = z
    .object({
    lastReadAt: z.string().datetime().nullable(),
    servedAt: z.string().datetime(),
})
    .strict();
/** Tope por minuto de `POST …/observations/:findingId/read` por identidad
 *  (el mismo que la marca de una consulta). */
export const OBSERVATION_READ_RATE_LIMIT = 60;
// Re-exported so apps/web-dashboard reaches the delegation vocabulary
// without taking a dependency edge on @bitacorapro/core-engine — it
// depends on api-types + shared only. These are values, not just types:
// the dashboard needs the transition map to decide which options a row's
// menu may offer, and re-deriving that client-side is exactly the drift
// ADR-0074 exists to prevent.
export { CLIENT_SETTABLE_DELEGATION_STATUSES, CLOSED_DELEGATION_STATUSES, DELEGATION_STATUSES, delegationNoteRequired, isOpenDelegationStatus, 
// Gate de confianza (D5): the dashboard's "Dejar como Otro" and the
// OTR-pending branch need the literal specialty code without a
// core-engine dependency edge.
OTR_ESPECIALIDAD, 
// The raw transition map is deliberately NOT re-exported: reading it
// requires knowing the private "__null__" origin key, which every
// consumer would then hand-copy. Ask the engine instead.
legalDelegationTargets, } from "@bitacorapro/core-engine";
// Same re-export posture for the ADR-0105 observation graph: the
// dashboard's per-state action panel derives its button set from
// legalObservationTargets — never a hand-maintained state→buttons map
// that can drift from the engine (the exact bug class ADR-0105 closed).
export { appealedObservationKeys, effectiveObservationSeverity, isObservationClosed, legalObservationTargets, observationPhase, observationPhaseKeys, } from "@bitacorapro/core-engine";
// POST /findings/:id/assign — la asignación de verdad.
//
// Hasta acá «asignar» clasificaba la especialidad y cambiaba el estado, y
// el enlace del contratista se emitía por un camino aparte, admin-only,
// desde otra pantalla. Eran dos actos desconectados, y la consecuencia no
// era cosmética: `findings.provider_id` NUNCA se escribía en postventa,
// así que «trabajos activos» por contratista y «quién tarda» eran
// incalculables, y una observación podía quedar en `asignada` sin enlace
// vigente.
//
// Un solo acto, entonces: elegir contratista, emitir su enlace y mover el
// estado, o nada.
// ADR-0134 — /assign y /assign-batch aceptan el proveedor por id o lo
// crean en línea (`newProvider`, la misma forma y el mismo
// crear-o-adjuntar que /delegate tenía desde ADR-0082/#0430): facility
// retira /delegate y su panel de asignación necesita seguir dando de alta
// proveedores sin pasar por la nómina. Exactamente uno de los dos.
/** F8 — la visita que el jefe agenda: un día de calendario de Santiago
 *  («AAAA-MM-DD», desde hoy hasta `OBSERVATION_VISIT_MAX_DAYS_AHEAD`) y un
 *  rango de horas de ese día («HH:MM», hora de Santiago, pasos de 30 min,
 *  el término después del inicio; corte de ajustes 2026-10-16). El
 *  servidor valida la ventana de días. */
export const findingVisitScheduleSchema = z
    .object({
    date: observationVisitDateSchema,
    startTime: observationVisitTimeSchema,
    endTime: observationVisitTimeSchema,
})
    .strict()
    .refine((v) => visitRangeValid(v.startTime, v.endTime), {
    message: "endTime must be after startTime",
    path: ["endTime"],
});
const exactlyOneProvider = (b) => Boolean(b.providerId) !== Boolean(b.newProvider);
const EXACTLY_ONE_PROVIDER = "exactly one of providerId or newProvider must be provided";
export const findingAssignBody = z
    .object({
    providerId: z.string().uuid().optional(),
    newProvider: newProviderSchema.optional(),
    note: z.string().max(500).optional(),
    /** Plazo del contratista en días CORRIDOS (nunca hábiles). Sin él, el
     *  servidor usa el de la receta por severidad (`observationDueDays`,
     *  facility: crítica 3 · moderada 7 · leve 10) o, si no la declara, el
     *  de la criticidad más alta (`DEFAULT_PLAZO_DIAS_BY_SEVERITY`: S1 3 ·
     *  S2 5 · S3 7 · S4 10). */
    plazoDias: z.number().int().min(1).max(365).optional(),
    /** F8 (corte 6) — la visita, opcional, en el mismo acto. Sin ella una
     *  reasignación BORRA la fecha vigente (el contratista que entra
     *  agenda de nuevo). Sólo con contraparte externa; en otra receta,
     *  409 `VISIT_NOT_ENABLED`. */
    visit: findingVisitScheduleSchema.optional(),
})
    .strict()
    .refine(exactlyOneProvider, { message: EXACTLY_ONE_PROVIDER });
export const findingAssignResponse = z.object({
    id: z.string().uuid(),
    providerId: z.string().uuid(),
    providerShortName: z.string(),
    /** Con `newProvider`: true si la fila se creó, false si se adjuntó a
     *  un proveedor existente del mismo nombre (mismo contrato que
     *  /delegate). null cuando vino `providerId`. Opcional por
     *  compatibilidad con clientes desplegados. */
    providerCreated: z.boolean().nullable().optional(),
    observationStatus: z.string(),
    observationStatusAt: z.string().datetime(),
    /** El enlace público recién emitido. Sin notificaciones propias, éste
     *  ES el canal: el panel lo muestra copiable. */
    url: z.string().url(),
    /** True cuando esto fue una reasignación y el enlace del contratista
     *  saliente quedó revocado en la misma transacción (decisión 11 del
     *  handoff del panel). Nunca hay una ventana sin enlace vigente. */
    revokedPreviousLink: z.boolean(),
    /** True sólo cuando el contratista tenía correo de contacto y auth-svc
     *  confirmó el envío de su enlace. False = el enlace copiable de `url`
     *  es el único canal; el panel debe decirlo. Opcional por
     *  compatibilidad con clientes desplegados. */
    contractorNotified: z.boolean().optional(),
    /** Vencimiento del plazo recién estampado (`observation_due_at`) y su
     *  largo en días corridos. Opcionales por compatibilidad. */
    dueAt: z.string().datetime().optional(),
    plazoDias: z.number().int().optional(),
    /** F8 — la visita vigente tras asignar (null si no se agendó). */
    visit: observationVisitSchema.nullable().optional(),
});
// POST /findings/assign-batch — asignar varias observaciones de un
// proyecto a un mismo contratista.
//
// Desvío consciente al spec §3.2 («una sola acción por fila»), declarado
// en el handoff del panel: rechazar sigue exigiendo abrir el detalle
// —nadie rechaza con un resumen— pero asignar en lote es la operación
// que el jefe hace de verdad cuando llegan seis filtraciones del mismo
// edificio.
//
// Dos reglas que la forma del contrato impone:
//
//   - **Un enlace POR UNIDAD.** El enlace público pertenece a una unidad
//     (`SHARE_BUNDLE_CROSS_ASSET`), así que una selección multi-unidad
//     produce varios enlaces, no uno. La respuesta los devuelve
//     agrupados por unidad para que el panel los ofrezca a copiar.
//   - **Asignar acepta implícitamente lo abierto.** Una observación que
//     todavía no se aceptó recorre las dos aristas, cada una con su
//     hito: el hilo tiene que poder mostrar que alguien la aceptó, aunque
//     ese alguien lo haya hecho de un tirón con otras cinco.
export const findingAssignBatchBody = z
    .object({
    // Sin repetidos: la misma observación dos veces en un lote produciría
    // dos enlaces (y dos hitos) en la misma transacción, con el segundo
    // revocando al primero antes de que nadie lo vea.
    findingIds: z
        .array(z.string().uuid())
        .min(1)
        .max(50)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: "findingIds must not contain duplicates",
    }),
    providerId: z.string().uuid().optional(),
    newProvider: newProviderSchema.optional(),
    /** Instrucciones para el contratista: viajan en su correo. */
    note: z.string().max(500).optional(),
    /** Plazo del contratista en días CORRIDOS (nunca hábiles). Sin él, el
     *  servidor usa el de la criticidad más alta
     *  (`DEFAULT_PLAZO_DIAS_BY_SEVERITY`: S1 3 · S2 5 · S3 7 · S4 10). */
    plazoDias: z.number().int().min(1).max(365).optional(),
    /** False = no enviar el correo de asignación al contratista (los
     *  enlaces se emiten igual y el panel los ofrece a copiar). Ausente
     *  equivale a true. Los correos al propietario por la aceptación
     *  implícita no dependen de esto: los gobierna la receta. */
    notificar: z.boolean().optional(),
    /** F8 — la misma visita para todo el lote (opcional, como en /assign). */
    visit: findingVisitScheduleSchema.optional(),
})
    .strict()
    .refine(exactlyOneProvider, { message: EXACTLY_ONE_PROVIDER });
export const findingAssignBatchResponse = z.object({
    providerId: z.string().uuid(),
    providerShortName: z.string(),
    /** Igual que en /assign. */
    providerCreated: z.boolean().nullable().optional(),
    /** Un enlace por unidad. `findingIds` dice cuáles de las seleccionadas
     *  cayeron en cada uno. */
    links: z.array(z.object({
        assetId: z.string().uuid(),
        assetLabel: z.string(),
        url: z.string().url(),
        findingIds: z.array(z.string().uuid()),
        /** True si auth-svc confirmó el correo al contratista para ESTA
         *  unidad. False si no se pidió notificar o si el envío falló: el
         *  panel ofrece este enlace a copiar (el token crudo sólo viaja en
         *  esta respuesta). Opcional por compatibilidad (ADR-0021 D6). */
        notified: z.boolean().optional(),
    })),
    /** Cuántas se aceptaron implícitamente en el camino. El panel lo dice
     *  en el toast: el jefe tiene que saber que aceptó, no sólo que
     *  asignó. */
    implicitlyAccepted: z.number().int().nonnegative(),
    /** True sólo si se pidió notificar y auth-svc confirmó TODOS los
     *  envíos al contratista (uno por unidad). Un envío parcial es false:
     *  `links[].notified` dice cuáles salieron. Opcional por
     *  compatibilidad. */
    contractorNotified: z.boolean().optional(),
    /** Vencimiento del plazo recién estampado (`observation_due_at`) y su
     *  largo en días corridos. Opcionales por compatibilidad. */
    dueAt: z.string().datetime().optional(),
    plazoDias: z.number().int().optional(),
});
// POST /findings/:id/visit — F8 (fase 2, corte 6, #0745): agendar o
// cambiar la visita del contratista DESPUÉS de asignar, mientras la
// observación sigue en sus manos. Lo hace el carril que reasigna (jefe en
// sus proyectos, gerente en la org); 403 a otro carril, 404 fuera de
// alcance, 409 `VISIT_STATUS_NOT_ELIGIBLE` si no está con el contratista,
// 409 `VISIT_NOT_ENABLED` en una receta sin contraparte externa
// (facility), 400 `VISIT_DATE_OUT_OF_RANGE` con un día pasado o
// demasiado lejano; 400 de validación con un término que no va después
// del inicio. Mismo día y rango que la vigente = `changed: false`,
// sin hito, sin correo y sin aviso. Admite Idempotency-Key (scope org +
// observación).
export const findingVisitBody = findingVisitScheduleSchema;
export const findingVisitResponse = z.object({
    id: z.string().uuid(),
    visit: observationVisitSchema,
    /** False cuando el día y el rango ya eran los vigentes. */
    changed: z.boolean(),
    /** True si el correo al propietario salió (o el propietario lo apagó
     *  en sus preferencias, ADR-0147); false si no se pudo avisar o no hubo
     *  cambio. */
    ownerNotified: z.boolean(),
    /** ADR-0147 (corte de ajustes) — qué pasó con el correo. Ausente sin
     *  cambio (no se avisa nada). */
    ownerNotice: ownerNoticeOutcomeSchema.optional(),
});
//# sourceMappingURL=findings.js.map